//ListingResults.tsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Star, MapPin, Award, Globe, Phone, Mail, Search } from "lucide-react";
import { Contractor } from "../../models/Contractor.interface";
import { formatAddress } from "../../models/Address.interface";
import { useContractorSearch } from "../../services/searchService";
import { CategorySelect } from "../../Components/CategorySelect";
import { LocationSelect } from "../../Components/LocationSelect";
import "./ContractorListing.css";
import Header from "../Home/Header";
import Footer from "../Home/Footer";

interface FilterState {
  rating: string;
  category: string;
  location: string;
}

const ITEMS_PER_PAGE = 20;
const MAX_VISIBLE_PAGES = 5;

const ListingResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { searchContractors } = useContractorSearch();
  const searchParams = new URLSearchParams(location.search);
  const initialQuery = searchParams.get("q") || "";

  const [searchInputValue, setSearchInputValue] = useState(initialQuery);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterInputs, setFilterInputs] = useState<FilterState>({
    rating: "",
    category: "",
    location: "",
  });
  const [activeFilters, setActiveFilters] = useState<FilterState>({
    rating: "",
    category: "",
    location: "",
  });

  useEffect(() => {
    fetchResults();
  }, [searchQuery, activeFilters, currentPage]);

  const fetchResults = async () => {
    setLoading(true);
    setError(null);

    try {
      const { contractors: results, totalCount } = await searchContractors({
        searchQuery,
        filters: activeFilters,
        page: currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
      });

      setContractors(results);
      setTotalResults(totalCount);
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to load search results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilterInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setActiveFilters(filterInputs);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchQuery(searchInputValue);
    const params = new URLSearchParams();
    if (searchInputValue) {
      params.set("q", searchInputValue);
    }
    navigate(`/search?${params.toString()}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let startPage = Math.max(
      1,
      currentPage - Math.floor(MAX_VISIBLE_PAGES / 2)
    );
    let endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);

    if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }

    const pages = [];

    // First page
    if (startPage > 1) {
      pages.push(
        <PaginationItem key="1">
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        pages.push(
          <PaginationItem key="ellipsis1" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={currentPage === i}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <PaginationItem key="ellipsis2" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      pages.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="justify-content-center">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink
            previous
            onClick={() => handlePageChange(currentPage - 1)}
          />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink
            next
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </PaginationItem>
      </Pagination>
    );
  };

  const renderContactInfo = (contractor: Contractor) => (
    <div className="contact-info mt-2">
      {contractor.phone && (
        <div className="mb-1">
          <Phone size={14} className="me-2" />
          <a href={`tel:${contractor.phone}`} className="text-decoration-none">
            {contractor.phone}
          </a>
        </div>
      )}
      {contractor.email && (
        <div className="mb-1">
          <Mail size={14} className="me-2" />
          <a
            href={`mailto:${contractor.email}`}
            className="text-decoration-none"
          >
            {contractor.email}
          </a>
        </div>
      )}
      {contractor.website && (
        <div className="mb-1">
          <Globe size={14} className="me-2" />
          <a
            href={contractor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none"
          >
            {contractor.website.replace(/^https?:\/\//, "")}
          </a>
        </div>
      )}
    </div>
  );

  const handleViewProfile = (contractor: Contractor) => {
    navigate(`/contractor-profile/${contractor.contractorId}`);
  };

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        {/* Search Header */}
        <div className="bg-light border-bottom py-3">
          <Container>
            <Row className="align-items-center">
              <Col>
                <div className="d-flex">
                  <Input
                    type="text"
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Search contractors..."
                    className="me-2"
                  />
                  <Button color="primary" onClick={handleSearch}>
                    <Search size={18} />
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            {/* Filters Sidebar */}
            <Col md={3} className="mb-4">
              <Card className="overflow-visible">
                <CardBody>
                  <h5 className="mb-4">Filters</h5>
                  <Form>
                    {/* Rating Filter */}
                    {/* <FormGroup className="mb-4">
                    <Label>Minimum Rating</Label>
                    <Input
                      type="select"
                      name="rating"
                      value={filterInputs.rating}
                      onChange={(e) =>
                        handleFilterChange(e.target.name, e.target.value)
                      }
                    >
                      <option value="">Any Rating</option>
                      <option value="4">4+ Stars</option>
                      <option value="4.5">4.5+ Stars</option>
                      <option value="5">5 Stars</option>
                    </Input>
                  </FormGroup> */}

                    {/* Category Filter */}
                    <FormGroup className="mb-4">
                      <Label>Category</Label>
                      <CategorySelect
                        value={filterInputs.category}
                        onChange={(value: string) => 
                          handleFilterChange("category", value)
                        }
                      />
                    </FormGroup>

                    {/* Location Filter */}
                    <FormGroup className="mb-4">
                      <Label>Location</Label>
                      <LocationSelect
                        value={filterInputs.location}
                        onChange={(value) =>
                          handleFilterChange("location", value)
                        }
                      />
                    </FormGroup>

                    <Button
                      color="primary"
                      block
                      onClick={handleApplyFilters}
                      className="mt-3"
                    >
                      Apply Filters
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>

            {/* Results */}
            <Col md={9}>
              {error && (
                <Alert color="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <h4>
                      {totalResults} results
                      {searchQuery && ` for "${searchQuery}"`}
                    </h4>
                  </div>

                  {contractors.map((contractor) => (
                    <Card key={contractor.id} className="p-1 mb-4">
                      <CardBody>
                        <Row>
                          <Col md={8}>
                            <div className="d-flex">
                              {contractor.logo ? (
                                <img
                                  src={contractor.logo}
                                  alt={contractor.businessName}
                                  className="rounded-circle object-fit-cover me-3"
                                  style={{ width: "64px", height: "64px" }}
                                />
                              ) : (
                                <div
                                  className="flex-shrink-0 me-3 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                  style={{ width: "64px", height: "64px" }}
                                >
                                  <span className="fs-5">
                                    {contractor.businessName.charAt(0)}
                                  </span>
                                </div>
                              )}
                              <div>
                                <h5 className="mb-1">
                                  {contractor.businessName}
                                </h5>
                                {contractor.address && (
                                  <p className="text-muted mb-2">
                                    <MapPin size={16} className="me-1" />
                                    <span className="text-wrap">
                                      {formatAddress(contractor.address)}
                                    </span>
                                  </p>
                                )}
                                <div className="mb-2">
                                  {contractor.categories?.map((category) => (
                                    <Badge
                                      key={category}
                                      color="secondary"
                                      className="me-2 mb-1"
                                    >
                                      {category}
                                    </Badge>
                                  ))}
                                </div>
                                {renderContactInfo(contractor)}
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="text-md-end">
                            {contractor.rating && (
                              <div className="mb-2">
                                <Star className="text-warning" size={16} />
                                <span className="ms-1">{contractor.rating}</span>
                                {contractor.reviewCount && (
                                  <span className="text-muted ms-1">
                                    ({contractor.reviewCount} reviews)
                                  </span>
                                )}
                              </div>
                            )}
                            {/*contractor.bzScore && (
                              <div className="mb-2">
                                <Award className="text-success" size={16} />
                                <span className="ms-1">
                                  BuildZoom Score: {contractor.bzScore}
                                </span>
                              </div>
                            )*/}
                            <div>
                              <Button
                                color="outline-primary"
                                size="sm"
                                onClick={() => handleViewProfile(contractor)}
                              >
                                View Profile
                              </Button>
                            </div>
                          </Col>
                        </Row>

                        {contractor.generalInfo && (
                          <Row className="mt-3">
                            <Col>
                              <p className="mb-0">
                                {contractor.generalInfo}
                              </p>
                            </Col>
                          </Row>
                        )}
                      </CardBody>
                    </Card>
                  ))}

                  <div className="d-flex justify-content-center">
                    {renderPagination()}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ListingResults;
